//#region IMPORT
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  InfoOutlined as InfoOutlinedIcon,
  Check as CheckIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Dialog, Button } from '../../../../Components/MUIs';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import { mapParamsBySchema } from '../../../../util';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { GON_BASIC_Schema } from '../../../BasicInfo/Gongjong/GON_BASIC/GON_BASIC_Schema';
import { MemberInfoModal_Schema } from '../../../../Components/MemberInfoModal/MemberInfoModal_Schema';
//#endregion

const ALL_DEP = { depCd: '__ALL__', depNmLast: '전체' };

function NormalizeMember(r) {
  return {
    id: r.id ?? r.Id ?? '',
    name: r.name ?? r.Name ?? '',
    depCd: (r.depCd ?? r.DepCd ?? '').trim(),
    depNm1: r.depNm1 ?? r.DepNm1 ?? '',
    depNm2: r.depNm2 ?? r.DepNm2 ?? '',
    depNm3: r.depNm3 ?? r.DepNm3 ?? '',
    depNm4: r.depNm4 ?? r.DepNm4 ?? '',
    position: r.position ?? r.Position ?? '',
    rank: r.rank ?? r.Rank ?? '',
    tel: r.tel ?? r.Tel ?? '',
  };
}

function MemberInDep(m, dep) {
  if (!dep || dep.depCd === ALL_DEP.depCd) return true;
  if (m.depCd && dep.depCd && m.depCd === dep.depCd) return true;
  const leaf =
    [m.depNm4, m.depNm3, m.depNm2, m.depNm1].find((x) => x && String(x).trim()) ||
    '';
  const depLabel = dep.depNmLast || '';
  return leaf && depLabel && leaf === depLabel;
}

function MatchesMemberQuery(m, q) {
  if (!q?.trim()) return true;
  const s = q.trim().toLowerCase();
  const blob = [
    m.name,
    m.position,
    m.rank,
    m.depNm1,
    m.depNm2,
    m.depNm3,
    m.depNm4,
    m.tel,
    m.id,
  ]
    .join(' ')
    .toLowerCase();
  return blob.includes(s);
}

function StringToAvatarColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h} 45% 46%)`;
}

function ChipLabel(m) {
  const rankOrPos = m.rank || m.position || '';
  const shortDep =
    m.depNm3 || m.depNm2 || m.depNm1 || '';
  const deptPart = shortDep ? ` (${shortDep})` : '';
  return `${m.name || ''} ${rankOrPos}`.trim() + deptPart;
}

/**
 * 결재자 선택 — 좌측 부서(조직) 목록, 우측 조직원, 하단 선택 칩 (스크린샷 유사)
 * @param {'intermediate'|'final'} mode
 * @param {number} maxCount — intermediate: 남은 슬롯, final: 1
 * @param {string[]} excludeMemberIds — 선택 불가 ID (예: 신청인, 이미 고른 중간결재자)
 */
function TRA_CUSTOM_02_ApproverSelectModal({
  open,
  onClose,
  onConfirm,
  mode = 'intermediate',
  maxCount = 2,
  excludeMemberIds = [],
}) {
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const searchInfo = useSelector((s) => s.SearchInfo);

  const [loading, setLoading] = useState(false);
  const [depList, setDepList] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [depSearch, setDepSearch] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [selectedDep, setSelectedDep] = useState(ALL_DEP);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const excludeSet = useMemo(
    () =>
      new Set(
        (excludeMemberIds || [])
          .filter(Boolean)
          .map((id) => String(id).trim())
          .filter(Boolean)
      ),
    [excludeMemberIds]
  );

  const companyLabel =
    searchInfo?.custNm || userInfo?.coNm || userInfo?.custNm || '조직';

  const resetLocal = useCallback(() => {
    setDepSearch('');
    setMemberQuery('');
    setSelectedDep(ALL_DEP);
    setSelectedMembers([]);
  }, []);

  const LoadData = useCallback(async () => {
    if (!searchInfo?.custCd) return;
    setLoading(true);
    try {
      const ctx = { userInfo, search: { ...searchInfo, searchText: '' } };
      const depParams = mapParamsBySchema(GON_BASIC_Schema.mapper.search1, ctx);
      const memParams = mapParamsBySchema(
        MemberInfoModal_Schema.mapper.search1,
        ctx
      );
      const [depRes, memRes] = await Promise.all([
        apiRequest('post', Global.urls.Utility.getDepLast, depParams),
        apiRequest('post', Global.urls.Member.getMemBasicR1, memParams),
      ]);

      handleQuerySuccess(depRes, {
        onSuccess: (rows) => {
          const mapped = (rows || []).map((r, i) => ({
            depCd: r.DepCd ?? r.depCd ?? '',
            depNmLast:
              r.DepNmLast ??
              r.depNmLast ??
              r.DepNm4 ??
              r.DepNm3 ??
              r.DepNm2 ??
              r.DepNm1 ??
              '',
            no: r.no ?? i + 1,
          }));
          setDepList(mapped);
        },
      });

      handleQuerySuccess(memRes, {
        onSuccess: (rows) => {
          setAllMembers((rows || []).map(NormalizeMember));
        },
      });
    } finally {
      setLoading(false);
    }
  }, [searchInfo, userInfo]);

  useEffect(() => {
    if (!open) {
      resetLocal();
      return;
    }
    LoadData();
  }, [open, LoadData, resetLocal]);

  const filteredDeps = useMemo(() => {
    const q = depSearch.trim().toLowerCase();
    if (!q) return depList;
    return depList.filter((d) =>
      (d.depNmLast || '').toLowerCase().includes(q)
    );
  }, [depList, depSearch]);

  const visibleMembers = useMemo(() => {
    return allMembers.filter((m) => {
      if (!MemberInDep(m, selectedDep)) return false;
      if (!MatchesMemberQuery(m, memberQuery)) return false;
      return true;
    });
  }, [allMembers, selectedDep, memberQuery]);

  const IsExcluded = (m) => {
    const id = m?.id != null ? String(m.id).trim() : '';
    return Boolean(id && excludeSet.has(id));
  };

  const IsSelected = (m) => selectedMembers.some((s) => s.id === m.id);

  const ToggleMember = (m) => {
    if (!m?.id || IsExcluded(m)) return;

    if (mode === 'final') {
      setSelectedMembers(IsSelected(m) ? [] : [m]);
      return;
    }

    if (IsSelected(m)) {
      setSelectedMembers((prev) => prev.filter((x) => x.id !== m.id));
      return;
    }
    if (selectedMembers.length >= maxCount) {
      return;
    }
    setSelectedMembers((prev) => [...prev, m]);
  };

  const HandleSelectAllVisible = () => {
    const eligible = visibleMembers.filter((m) => !IsExcluded(m));
    if (mode === 'final') {
      setSelectedMembers(eligible[0] ? [eligible[0]] : []);
      return;
    }
    const merged = [];
    const seen = new Set();
    for (const x of selectedMembers) {
      merged.push(x);
      seen.add(x.id);
    }
    for (const m of eligible) {
      if (merged.length >= maxCount) break;
      if (!seen.has(m.id)) {
        merged.push(m);
        seen.add(m.id);
      }
    }
    setSelectedMembers(merged);
  };

  const HandleRemoveChip = (id) => {
    setSelectedMembers((prev) => prev.filter((x) => x.id !== id));
  };

  const HandleConfirm = () => {
    if (!selectedMembers.length) {
      return;
    }
    onConfirm?.(mode === 'final' ? [selectedMembers[0]] : [...selectedMembers]);
  };

  const HandleClose = () => {
    resetLocal();
    onClose?.();
  };

  const title = '결재자 선택';
  const countLabel = visibleMembers.length;

  return (
    <Dialog
      open={open}
      onClose={HandleClose}
      title={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <IconButton size="small" aria-label="닫기" onClick={HandleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      }
      useCloseButton={false}
      useSaveButton={false}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          maxHeight: '92vh',
          width: 'min(1100px, 96vw)',
        },
      }}
      contentSx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: 360,
              borderBottom: '1px solid #e8ecf0',
              overflow: 'hidden',
            }}
          >
            {/* 좌: 조직도 */}
            <Paper
              elevation={0}
              sx={{
                width: 280,
                flexShrink: 0,
                borderRight: '1px solid #e8ecf0',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#fafbfc',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ px: 2, py: 1.5, fontWeight: 700, color: 'text.secondary' }}
              >
                조직도
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="부서 검색"
                value={depSearch}
                onChange={(e) => setDepSearch(e.target.value)}
                sx={{ px: 1.5, mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="caption"
                sx={{ px: 2, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}
              >
                {companyLabel}
              </Typography>
              <List dense sx={{ overflow: 'auto', flex: 1, py: 0 }}>
                <ListItemButton
                  selected={selectedDep?.depCd === ALL_DEP.depCd}
                  onClick={() => setSelectedDep(ALL_DEP)}
                  sx={{ py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <FolderIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={ALL_DEP.depNmLast} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
                {filteredDeps.map((d) => (
                  <ListItemButton
                    key={d.depCd || d.depNmLast}
                    selected={selectedDep?.depCd === d.depCd && d.depCd !== ALL_DEP.depCd}
                    onClick={() => setSelectedDep(d)}
                    sx={{ py: 0.75 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FolderIcon fontSize="small" sx={{ color: '#78909c' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={d.depNmLast || d.depCd}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            {/* 우: 조직원 */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="이름, 직위, 직책, 부서, 전화, 아이디"
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                sx={{ px: 2, pt: 1.5, pb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  pb: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  조직원 ({countLabel}명)
                </Typography>
                <Button
                  name="전체 선택"
                  variant="text"
                  onClick={HandleSelectAllVisible}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                />
              </Box>

              <TableContainer sx={{ flex: 1, overflow: 'auto', px: 1 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={36} />
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ bgcolor: '#f5f7fa' }} />
                        <TableCell sx={{ bgcolor: '#f5f7fa', fontWeight: 700 }}>
                          이름
                        </TableCell>
                        <TableCell sx={{ bgcolor: '#f5f7fa', fontWeight: 700 }}>
                          직위 / 직책
                        </TableCell>
                        <TableCell sx={{ bgcolor: '#f5f7fa', fontWeight: 700 }}>
                          부서
                        </TableCell>
                        <TableCell align="center" sx={{ bgcolor: '#f5f7fa', fontWeight: 700, width: 88 }}>
                          관리
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleMembers.map((m) => {
                        const excluded = IsExcluded(m);
                        const sel = IsSelected(m);
                        const depStr = [m.depNm1, m.depNm2, m.depNm3, m.depNm4]
                          .filter(Boolean)
                          .join(' ');
                        const rankPos = [m.rank, m.position].filter(Boolean).join(' / ');
                        return (
                          <TableRow
                            key={m.id || m.name}
                            hover
                            sx={{
                              opacity: excluded ? 0.5 : 1,
                              bgcolor: sel ? 'rgba(21, 101, 192, 0.06)' : 'inherit',
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={sel}
                                disabled={excluded || (!sel && mode === 'intermediate' && selectedMembers.length >= maxCount)}
                                onChange={() => ToggleMember(m)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: 14,
                                    bgcolor: StringToAvatarColor(m.name),
                                  }}
                                >
                                  {(m.name || '?').charAt(0)}
                                </Avatar>
                                <Typography variant="body2" fontWeight={600}>
                                  {m.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {rankPos || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" noWrap title={depStr}>
                                {depStr || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {excluded ? (
                                <CheckIcon sx={{ color: '#b0bec5' }} fontSize="small" />
                              ) : sel ? (
                                <IconButton size="small" disabled sx={{ color: 'success.main' }}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: '#fff',
                                    bgcolor: '#2e7d32',
                                    '&:hover': { bgcolor: '#1b5e20' },
                                  }}
                                  onClick={() => ToggleMember(m)}
                                  disabled={
                                    mode === 'intermediate' &&
                                    selectedMembers.length >= maxCount
                                  }
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Box>
          </Box>

          {/* 선택 칩 + 안내 + 버튼 */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafbfc' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              선택된 결재자:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
              {selectedMembers.map((m) => (
                <Chip
                  key={m.id}
                  label={ChipLabel(m)}
                  onDelete={() => HandleRemoveChip(m.id)}
                  size="small"
                  sx={{ bgcolor: '#fff', border: '1px solid #cfd8dc' }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              px: 2,
              py: 2,
              borderTop: '1px solid #e8ecf0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 480 }}>
              <InfoOutlinedIcon sx={{ color: 'info.main', mt: 0.25, fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                선택한 멤버는 병렬 결재자로 추가됩니다.
                {mode === 'intermediate' && maxCount > 0
                  ? ` (최대 ${maxCount}명까지 선택 가능)`
                  : null}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button name="취소" variant="text" onClick={HandleClose} sx={{ minWidth: 72 }} />
              <Button
                name="확인"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={HandleConfirm}
                disabled={!selectedMembers.length}
                sx={{
                  minWidth: 96,
                  bgcolor: '#1565c0',
                  color: '#fff',
                  '&:hover': { bgcolor: '#0d47a1' },
                }}
              />
            </Box>
          </Box>
        </Box>
      }
    />
  );
}

export default TRA_CUSTOM_02_ApproverSelectModal;
